import { useEffect, useRef, useState } from "react";
import { stepTextToShow } from "@builder/assist/ui/helpers/wordingHelpers";

export interface onboardStepsToShow extends stepTextToShow {
    id: number;
}

export const SHORT_ACTIVITIES_CREATING_POS: stepTextToShow[] = [
    { present: "Adding navigation", past: "Added navigation" },
    { present: "Adding some layouts", past: "Added some layouts" },
    { present: "Creating a product list", past: "Created a product list" },
    { present: "Creating a product detail page", past: "Created a product detail page" },
    { present: "Creating a cart page", past: "Created a cart page" },
    { present: "Creating a checkout page", past: "Created a checkout page" },
    { present: "Creating a thank you page", past: "Created a thank you page" }
];

export function useGenerateActivitiesWithMinimalInterval({
    isDone,
    activityArray,
    options = {}
}: {
    isDone?: boolean;
    activityArray: stepTextToShow[];
    options?: {
        interval?: number;
        maxArrayLength?: number;
        stopWhenReachedEnd?: boolean;
    };
}): onboardStepsToShow[] {
    const {
        interval = 2500,
        maxArrayLength = 100,
        stopWhenReachedEnd = true
    } = options;

    const currentId = useRef(0);
    const activityIdxRef = useRef(0);

    const [currentActions, setCurrentActions] = useState<onboardStepsToShow[]>(
        activityArray.length > 0 ? [{ id: currentId.current, ...activityArray[0] }] : []
    );

    useEffect(() => {
        if (isDone || activityArray.length === 0) return;

        const id = setInterval(() => {
            setCurrentActions(prev => {
                // if we should stop and we've reached the end of the array, do nothing
                if (
                    stopWhenReachedEnd &&
                    activityIdxRef.current >= activityArray.length - 1
                ) {
                    return prev;
                }

                currentId.current += 1;
                activityIdxRef.current += 1;

                const nextActivity =
                    activityArray[
                        stopWhenReachedEnd
                            ? activityIdxRef.current
                            : activityIdxRef.current % activityArray.length
                    ];

                // If no nextActivity (e.g., array hasn't caught up yet), don't add
                if (!nextActivity) return prev;

                return [
                    ...prev,
                    { id: currentId.current, ...nextActivity }
                ].slice(-maxArrayLength);
            });
        }, interval);

        return () => clearInterval(id);
    }, [isDone, interval, maxArrayLength, activityArray, stopWhenReachedEnd]);

    return currentActions;
}
